import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { NgbDate, NgbDateParserFormatter, NgbCalendar, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { ErrorStrings, ValidationPatterns } from 'src/app/constants/form.constants';
import { UserAddress, UserAddressType, UserTelephoneType } from 'src/app/interfaces/user';
import { AddressFinderService, AddressPAF } from 'src/app/services/address-finder/address-finder.service';
import { CommonUtilService } from 'src/app/services/common/common-util.service';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements OnInit {
  @Input() address: UserAddress;
  @Input() showButton ? = true;
  @Input() showLoader ? = false;
  @Output() save: EventEmitter<any> = new EventEmitter();

  errorStrings = ErrorStrings;
  validationPatterns = ValidationPatterns;
  fromDate: NgbDate;
  fullForm: boolean;
  hoveredDate: NgbDate = null;
  toDate: NgbDate;
  today: NgbDate;
  minDate: NgbDateStruct;

  userAddressType = UserAddressType;
  foundAddresses: AddressPAF[] = [];
  selectedAddress: AddressPAF = null;
  postcodeNotFound = false;
  postcodeFinderLoading = false;

  selectableAddressTypes: any[] = [
    {
      name: `Holiday home`,
      value: UserAddressType.HolidayHome
    },
    {
      name: `Carer's home`,
      value: UserAddressType.CarersHome
    },
    {
      name: `Relative's home`,
      value: UserAddressType.RelativesHome
    },
    {
      name: `Holiday Destination`,
      value: UserAddressType.HolidayDestination
    },
    {
      name: `Carer's Address`,
      value: UserAddressType.CarersAddress
    },
    {
      name: `Kennel or lodgings`,
      value: UserAddressType.KennelOrLodgings
    },
    {
      name: `Other`,
      value: UserAddressType.Other
    }
  ];

  constructor(
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    public addressFinder: AddressFinderService,
    private commonUtilService: CommonUtilService,
  ) {
    this.today = this.calendar.getToday();
    this.minDate = {
      day: 1,
      month: 1,
      year: this.today.year - 99
    };
  }

  createNgbDate(date: Date): NgbDate {
    return NgbDate.from({
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    });
  }

  findAddress(postcode: string): void {
    this.postcodeFinderLoading = true;
    this.addressFinder.getFromPostcode(postcode)
      .subscribe(
        foundAddresses => {
          this.foundAddresses = foundAddresses;
          this.postcodeNotFound = !foundAddresses.length;
        },
        err => {
          this.postcodeNotFound = true;
        }
      )
      .add(() => {
        this.postcodeFinderLoading = false;
      });
  }

  onAddressSelect(address: AddressPAF) {
    const addressLines = this.addressFinder.getAddressLines(address);
    this.address.line1 = addressLines[0];
    this.address.line2 = addressLines[1] ? addressLines[1] : '';
    this.address.town = address.town;
    this.address.county = '';

    this.foundAddresses = [];

    this.openFullForm();
  }

  updateValueAndValidity(control: AbstractControl): void {
    control.markAsTouched();
    setTimeout(() => control.updateValueAndValidity(), 0);
  }

  openFullForm(): void {
    this.fullForm = true;
    this.postcodeNotFound = false;
  }

  closeFullForm(): void {
    this.fullForm = false;
  }

  submit(addressForm): void {
    if (this.address.temporary) {
      this.address.temporary.dateStart = this.commonUtilService.createDateObject(this.fromDate);
      this.address.temporary.dateEnd = this.commonUtilService.createDateObject(this.toDate);
    }
    this.save.emit(null);
  }

  ngOnInit(): void {
    // This is required when the form is used to update an existing address
    if (this.address.addressType !== UserAddressType.Main && this.address.temporary) {
      this.fromDate = this.address.temporary.dateStart
        ? this.createNgbDate(this.address.temporary.dateStart)
        : null;
      this.toDate = !!this.address.temporary.dateEnd
        ? this.createNgbDate(this.address.temporary.dateEnd)
        : null;
    }
    if (!this.address.telephone) {
      this.address.telephone = {
        name: null,
        number: null,
        numberType: UserTelephoneType.Main
      };
    }
    this.fullForm = !!this.address.postcode;
  }
}
